import { apiClient } from './client';
import { useConfigStore } from '@/store/config.store';
import { 
  Task, 
  TaskWithConflicts,
  TaskResponse,
  CalendarTasksResponse, 
  NotionTestResponse,
  TaskStats 
} from '@/types/task.types';

export const tasksService = {
  // Get tasks for calendar view
  async getCalendarTasks(startDate: string, endDate: string): Promise<CalendarTasksResponse> {
    const { data } = await apiClient.get<CalendarTasksResponse>('/tasks/calendar', {
      params: {
        startDate,
        endDate
      }
    });
    return data;
  },

  // Get all tasks
  async getTasks(): Promise<Task[]> {
    const { data } = await apiClient.get<Task[]>('/notion/traffic/tasks');
    return data;
  },

  // Test Notion connection
  async testNotion(): Promise<NotionTestResponse> {
    const { data } = await apiClient.get<NotionTestResponse>('/notion/test');
    return data;
  },

  // Get a single task by ID (future implementation)
  async getTask(id: string): Promise<Task> {
    const { data } = await apiClient.get<Task>(`/notion/traffic/tasks/${id}`);
    return data;
  },

  // Create a new task
  async createTask(task: Partial<Task>): Promise<TaskWithConflicts> {
    // Get async mode from store (no API call needed!)
    const asyncMode = useConfigStore.getState().getAsyncMode();
    
    const response = await apiClient.post<TaskResponse>('/tasks', task, {
      params: {
        async: asyncMode.create
      }
    });
    
    // LOG DES CONFLITS POUR DEBUG
    if (response.data?.conflicts && response.data.conflicts.length > 0) {
      console.log('🚨 CONFLITS DÉTECTÉS (création):', response.data.conflicts);
      console.log('Response complète:', response.data);
    } else {
      console.log('✅ Pas de conflits détectés (création)');
      console.log('Response:', response.data);
    }
    
    // Backend returns { success: true, data: {...task}, conflicts: [...] }
    // Return task with conflicts attached
    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      const taskWithConflicts: TaskWithConflicts = {
        ...response.data.data,
        conflicts: response.data.conflicts
      };
      return taskWithConflicts;
    }
    
    // Fallback if response structure is different
    return response.data as TaskWithConflicts;
  },

  // Update a task
  async updateTask(id: string, task: Partial<Task>): Promise<TaskWithConflicts> {
    // Get async mode from store (no API call needed!)
    const asyncMode = useConfigStore.getState().getAsyncMode();
    
    // Mode normal - Utilise la configuration async du store
    const response = await apiClient.put<TaskResponse>(`/tasks/${id}`, task, {
      params: {
        async: asyncMode.update
      }
    });
    
    // LOG DES CONFLITS POUR DEBUG (optionnel - enlever en production)
    if (response.data?.conflicts && response.data.conflicts.length > 0) {
      console.log('🚨 CONFLITS DÉTECTÉS:', response.data.conflicts);
      console.log('Response complète:', response.data);
    }
    
    // Backend returns { success: true, data: {...task}, conflicts: [...] }
    // Return task with conflicts attached
    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      const taskWithConflicts: TaskWithConflicts = {
        ...response.data.data,
        conflicts: response.data.conflicts
      };
      return taskWithConflicts;
    }
    
    // Fallback if response structure is different
    return response.data as TaskWithConflicts;
  },

  // Delete a task
  async deleteTask(id: string): Promise<void> {
    // Get async mode from store (no API call needed!)
    const asyncMode = useConfigStore.getState().getAsyncMode();
    
    await apiClient.delete(`/tasks/${id}`, {
      params: {
        async: asyncMode.delete
      }
    });
  },

  // Get today's task statistics
  async getTodayStats(): Promise<TaskStats> {
    const response = await apiClient.get('/tasks/stats/today');
    return response.data.data;
  }
};